"use client";

type AnalyticsEvent = {
  event: string;
  category?: string;
  label?: string;
  value?: number;
  timestamp: number;
};

const ANALYTICS_KEY = "devstackio_analytics";
const MAX_EVENTS = 100;

function getStoredEvents(): AnalyticsEvent[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = sessionStorage.getItem(ANALYTICS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function storeEvent(event: AnalyticsEvent): void {
  if (typeof window === "undefined") return;
  try {
    const events = getStoredEvents();
    events.push(event);
    if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
    sessionStorage.setItem(ANALYTICS_KEY, JSON.stringify(events));
  } catch {}
}

function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem("cookie-consent");
    if (!raw) return false;
    return (JSON.parse(raw) as { analytics?: boolean })?.analytics === true;
  } catch {
    return false;
  }
}

function sendGtagEvent(event: string, options?: { category?: string; label?: string; value?: number }): void {
  // Official GA4 event syntax (https://developers.google.com/analytics/devguides/collection/ga4/events):
  // gtag('event', '<event_name>', {<event_parameters>}) — must run below the
  // Google tag snippet. Gated on analytics consent: no event leaves the
  // browser before opt-in.
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  try {
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== "function") return;
    const params: Record<string, unknown> = {};
    if (options?.category !== undefined) params.event_category = options.category;
    if (options?.label !== undefined) params.event_label = options.label;
    if (options?.value !== undefined) params.value = options.value;
    gtag("event", event, params);
  } catch {
    // analytics must never break tool functionality
  }
}

export function trackEvent(event: string, options?: { category?: string; label?: string; value?: number }): void {
  const analyticsEvent: AnalyticsEvent = {
    event,
    category: options?.category,
    label: options?.label,
    value: options?.value,
    timestamp: Date.now(),
  };
  storeEvent(analyticsEvent);

  // page_view is sent by the Analytics layout component via
  // gtag('config', ID, {page_path}) — forwarding it here as an event as well
  // would double-count (see https://nextjs.org/docs/app/guides/third-party-libraries).
  if (event === "page_view") return;
  sendGtagEvent(event, options);
}

export function trackToolUsage(toolSlug: string, action: "view" | "use" | "copy" | "export"): void {
  trackEvent("tool_interaction", {
    category: "tools",
    label: `${toolSlug}:${action}`,
  });
}

export function trackSearch(query: string, resultCount: number): void {
  // Privacy: the raw query stays in the local session buffer only — the GA
  // event carries just the result count, never user-typed text (which may
  // contain PII and must not leave the browser without explicit need).
  const analyticsEvent: AnalyticsEvent = {
    event: "search",
    category: "navigation",
    label: query,
    value: resultCount,
    timestamp: Date.now(),
  };
  storeEvent(analyticsEvent);
  sendGtagEvent("search", { category: "navigation", value: resultCount });
}

export function trackPageView(path: string): void {
  trackEvent("page_view", {
    category: "navigation",
    label: path,
  });
}

export function trackError(error: string, source?: string): void {
  trackEvent("error", {
    category: "error",
    label: source ? `${source}: ${error}` : error,
  });
}

export function trackPerformance(metric: string, value: number): void {
  trackEvent("performance", {
    category: "performance",
    label: metric,
    value,
  });
}

export function getAnalyticsSummary(): Record<string, number> {
  const events = getStoredEvents();
  const summary: Record<string, number> = {};
  events.forEach((e) => {
    summary[e.event] = (summary[e.event] || 0) + 1;
  });
  return summary;
}
